import createCachedSelector from 're-reselect';

import {
  selectAllScheduleAvailabilities,
  selectScheduleAvailabilitiesWhereAttribute,
  selectScheduleAvailabilityRelationshipId,
} from 'features/scheduleBuilder/slices/scheduleAvailabilities/slice';

import { emptyArray } from '../utils/common';
import { sortByNameCompareFromJob } from '../utils/sortByNameCompareFromJob';

import {
  selectJobIdArchivedAtMap,
  selectJobsForCurrentLocation,
} from './index';

export const selectScheduleAvailabilitiesForJobOnDate = createCachedSelector(
  (state, { jobId }) => jobId,
  selectAllScheduleAvailabilities,
  (state, { date }) => date,
  (jobId, scheduleAvailabilities, date) =>
    scheduleAvailabilities
      .filter(sa => sa.job?.id === jobId)
      .filter(sa => sa.date === date)
)((_, { jobId, date }) => `schedule-availabilities-job-${jobId}-date-${date}`);

export const selectScheduleAvailabilitiesForJobOnDateWithTitle =
  createCachedSelector(
    (state, { jobId, date }) => {
      const availabilities = selectScheduleAvailabilitiesForJobOnDate(state, {
        jobId,
        date,
      });
      return availabilities.length === 0 ? emptyArray : availabilities;
    },

    (state, { title }) => title,
    (availabilities, title) =>
      availabilities
        .filter(availability => availability.title === title)
        .map(availability => availability.id)
  )(
    (_, { jobId, date, title }) =>
      `preferred-schedule-availabilities-job-${jobId}-date-${date}-title-${title}`
  );

export const selectUnavailabilitiesForWholeDay = createCachedSelector(
  (state, { date }) => {
    const jobIds = selectJobsForCurrentLocation(state).map(j => j.id);
    const archivedJobIds = Object.keys(selectJobIdArchivedAtMap(state));
    const availabilities = selectScheduleAvailabilitiesWhereAttribute(state, {
      attribute: 'date',
      value: date,
    });

    const filteredAvailabilities = availabilities.filter(
      a =>
        a.title === 'Unavailable' &&
        a.allDay &&
        jobIds.includes(a?.job?.id) &&
        !archivedJobIds.includes(a?.job?.id)
    );

    const sortedAvailabilities = filteredAvailabilities.sort(
      sortByNameCompareFromJob({
        state,
        selectJobId: selectScheduleAvailabilityRelationshipId,
      })
    );

    return sortedAvailabilities.map(availability => availability.id).join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
)((_, { date }) => `selectUnavailabilitiesForWholeDay-${date}`);

export const selectUnavailabilitiesForPartialDay = createCachedSelector(
  (state, { date }) => {
    const jobIds = selectJobsForCurrentLocation(state).map(j => j.id);
    const archivedJobIds = Object.keys(selectJobIdArchivedAtMap(state));
    const availabilities = selectScheduleAvailabilitiesWhereAttribute(state, {
      attribute: 'date',
      value: date,
    });

    return availabilities
      .filter(
        a =>
          a.title === 'Unavailable' &&
          !a.allDay &&
          jobIds.includes(a?.job?.id) &&
          !archivedJobIds.includes(a?.job?.id)
      )
      .sort((a, b) => (new Date(a.startAt) < new Date(b.startAt) ? -1 : 1))
      .map(availability => availability.id)
      .join(',');
  },
  ids => (ids.length ? ids.split(',') : [])
)((_, { date }) => `selectUnavailabilitiesForPartialDay-${date}`);
